import React from 'react';
import { Dialog, Box, Typography, Button, IconButton, Grid } from '@mui/material';
import {
  FacebookShareButton, FacebookIcon,
  TwitterShareButton, TwitterIcon,
  LinkedinShareButton, LinkedinIcon,
  EmailShareButton, EmailIcon,
  WhatsappShareButton, WhatsappIcon
} from 'react-share';
import { showMessage } from '../utils';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const ShareDialog = ({
  open,
  onClose,
  shareUrl,
  shareTitle,
  shareToApp,
  handleCopyLink
}) => {
  // Backup copy function
  const copyTextToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    
    document.body.appendChild(textArea);
    
    try {
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      if (successful) {
        showMessage('Link copied successfully!', 'success');
      } else {
        showMessage('Failed to copy link', 'error');
      }
    } catch (err) {
      showMessage('Failed to copy link', 'error');
    } finally {
      document.body.removeChild(textArea);
    }
  };

  // Handle copy button click
  const handleCopyClick = () => {
    if (typeof handleCopyLink === 'function') {
      handleCopyLink(shareUrl);
    } else {
      copyTextToClipboard(shareUrl);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          maxWidth: { xs: '100%', sm: '600px' },
          mx: 'auto',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ 
        p: { xs: 2, sm: 4 }, 
        position: 'relative',
        textAlign: 'center',
      }}>
        <IconButton 
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#666',
            bgcolor: '#F5F5F5',
            width: 32,
            height: 32,
            '&:hover': {
              bgcolor: '#EAEAEA',
              color: '#333',
            },
            zIndex: 1,
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
        
        <Box sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 4,
          mt: 1,
        }}>
          <Box sx={{ 
            textAlign: 'left',
            flex: 1,
            pr: { xs: 0, sm: 3 },
            mb: { xs: 3, sm: 0 }
          }}>
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: "24px", sm: "32px" },
                fontWeight: 700,
                color: '#333',
                mb: 2,
                lineHeight: 1.2,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              }}
            >
              Refer Friends. Get Paid in Real Cash.
            </Typography>
            <Typography
              sx={{
                fontSize: "16px",
                color: '#666',
                mb: 2,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                lineHeight: 1.5,
              }}
            >
              Earn up to $100,000+ per referral — We'll mail you a check for every successful client you bring in.
            </Typography>
          </Box>
          
          <Box 
            component="img"
            src={`${process.env.PUBLIC_URL}/images/referral.jpg`}
            alt="Referral"
            sx={{
              width: { xs: '140px', sm: '180px' },
              height: 'auto',
              display: { xs: 'block', sm: 'block' }
            }}
          />
        </Box>
        
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 500,
            mb: 2,
            color: '#555',
            textAlign: 'left',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          Share this referral link to your friends and followers
        </Typography>
        
        {/* Link copy area */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "#F5F7FA",
            borderRadius: "8px",
            p: 1.5,
            gap: 1,
            mb: 3,
            border: '1px solid #E5E7EB',
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontSize: "14px",
              color: '#666',
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              textAlign: 'left',
              px: 1,
            }}
          >
            {shareUrl}
          </Typography>
          <Button
            variant="contained"
            onClick={handleCopyClick}
            startIcon={<ContentCopyIcon />}
            sx={{ 
              bgcolor: "#4285F4",
              color: "#FFF",
              fontWeight: 600,
              borderRadius: "8px",
              textTransform: 'none',
              px: 2,
              '&:hover': {
                bgcolor: '#3367D6'
              },
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              minWidth: '120px',
            }}
          >
            Copy link
          </Button>
        </Box>
        
        {/* Social media icons */}
        <Box sx={{ 
          display: 'flex',
          textAlign: 'center',
          justifyContent: 'center',
          gap: 4,
          mb: 2
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <FacebookShareButton
              url={shareUrl}
              quote={shareTitle}
              hashtag="#uTaxes"
              onClick={() => shareToApp && shareToApp("Facebook")}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 40,
                  height: 40,
                }}
              >
                <FacebookIcon size={40} round />
              </Box>
            </FacebookShareButton>
          </Box>
          
          <Box sx={{ textAlign: 'center' }}>
            <TwitterShareButton
              url={shareUrl}
              title={shareTitle}
              hashtags={["uTaxes", "TaxFiling"]}
              onClick={() => shareToApp && shareToApp("Twitter")}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 40,
                  height: 40,
                }}
              >
                <TwitterIcon size={40} round />
              </Box>
            </TwitterShareButton>
          </Box>
          
          <Box sx={{ textAlign: 'center' }}>
            <LinkedinShareButton
              url={shareUrl}
              title={shareTitle}
              onClick={() => shareToApp && shareToApp("Linkedin")}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 40,
                  height: 40,
                }}
              >
                <LinkedinIcon size={40} round />
              </Box>
            </LinkedinShareButton>
          </Box>
          
          <Box sx={{ textAlign: 'center' }}>
            <WhatsappShareButton
              url={shareUrl}
              title={shareTitle}
              onClick={() => shareToApp && shareToApp('Whatsapp')}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 40,
                  height: 40,
                }}
              >
                <WhatsappIcon size={40} round />
              </Box>
            </WhatsappShareButton>
          </Box>
        </Box>
      
      </Box>
    </Dialog>
  );
};

export default ShareDialog; 